import type {RecoveryControls, RecoveryRole, RecoveryTrigger} from '@github-ui/ahp-relay/relay-client-recovery'
import type {BrowserObservationTime} from './browser-observation-clock'
import type {UiReadinessSurface, UiReadinessVisibility} from './ui-readiness-contract'

export const CLIENT_RECOVERY_EVENT = 'one_copilot.web.client_recovery'
export const CLIENT_RECOVERY_POLICY = {
  policy_version: 'client_recovery_v2',
  gate_policy: 'one_copilot_browser_telemetry',
  sampling_policy: 'all_flagged_eligible_v1',
  eligibility_policy: 'selected_conversation_interruption_v2',
  disposition_policy: 'retained_or_explicit_terminal_v2',
} as const
export const RECOVERY_SURFACES = ['chat_app', 'global_task', 'repository_task', 'embedded_chat'] as const
export type RecoverySurface = (typeof RECOVERY_SURFACES)[number]
export const RECOVERY_STAGES = [
  'reconnect',
  'state_application',
  'pending_commands',
  'client_tools',
  'controls',
] as const
export type RecoveryStage = (typeof RECOVERY_STAGES)[number]
export type RecoveryPath = 'active_reattach' | 'resume' | 'host_restart' | 'unknown'
export type RecoveryManifest = 'complete' | 'unknown'
export type RecoveryInput =
  | 'credentials'
  | 'provided_state'
  | 'applied_state'
  | 'pending_manifest'
  | 'tool_manifest'
  | 'role_and_view'
export type RecoverySourceKind =
  | 'credentials'
  | 'reconnect_initiated'
  | 'handshake'
  | 'snapshot'
  | 'replay'
  | 'action'
  | 'prestate'
  | 'controls'
export type RecoveryReason =
  | 'prerequisite_unavailable'
  | 'prestate_unknown'
  | 'revision_unknown'
  | 'item_unresolved'
  | 'role_unknown'
  | 'controls_unobserved'
  | 'visibility_interrupted'
  | 'view_detached'
  | 'state_unverified'
  | 'binding_mismatch'
  | 'state_mismatch'
  | 'controls_mismatch'
  | 'client_ownership_lost'
  | 'client_command_lost'
  | 'disposed'
  | 'clock_unresolved'
  | 'observation_capacity'
  | 'entry_unverified'

export interface RecoveryBinding {
  readonly task_id: string
  readonly session_id: string
  readonly environment_id: string
  readonly chat_id?: string
}

export interface RecoveryItem {
  readonly item_kind: 'command' | 'client_tool' | 'view'
  readonly item_id: string
  readonly revision_id?: string
  readonly turn_id?: string
}

export interface RecoveryInventory {
  readonly commands_manifest: RecoveryManifest
  readonly tools_manifest: RecoveryManifest
  readonly views_manifest: RecoveryManifest
  readonly items: readonly RecoveryItem[]
}

export interface RecoverySource {
  readonly source_kind: RecoverySourceKind
  readonly source_session_id?: string
  readonly source_chat_id?: string
  readonly state_path?: 'snapshot' | 'replay' | 'unknown'
  readonly before_host_instance_id?: string
  readonly host_instance_id?: string
  readonly authority?: 'host_instance_v1'
  readonly client_relation?: 'same' | 'changed' | 'unknown'
}

export interface RecoverySourceReference extends RecoverySource {
  readonly event_id: string
  readonly attempt_event_id?: string
  readonly time: BrowserObservationTime
}

export interface RecoveryDisposition {
  readonly disposition:
    | 'retained'
    | 'applied'
    | 'completed'
    | 'transferred'
    | 'user_withdrawn'
    | 'system_failed'
    | 'unresolved'
  readonly evidence_kind:
    | 'snapshot_retained'
    | 'replay_retained'
    | 'host_action'
    | 'host_snapshot_terminal'
    | 'local_withdrawal'
    | 'unknown'
  readonly native_failure_code?: 'client_tool_disconnected' | 'client_tool_timeout' | 'client_tool_turn_ended'
}

export type RecoveryObservation =
  | ({
      readonly observation: 'episode_started'
      readonly commands_applicability: 'required' | 'known_empty'
      readonly tools_applicability: 'required' | 'known_empty'
      readonly command_count: number
      readonly tool_count: number
      readonly view_count: number
    } & Omit<RecoveryInventory, 'items'>)
  | ({readonly observation: 'item_declared'; readonly item_declaration_event_id: string} & RecoveryItem)
  | {readonly observation: 'attempt_started'; readonly previous_attempt_event_id?: string}
  | ({readonly observation: 'source_observed'; readonly source_event_id: string} & RecoverySource)
  | {
      readonly observation: 'stage_available'
      readonly stage: RecoveryStage
      readonly source_event_id: string
      readonly input_kind: RecoveryInput
    }
  | {
      readonly observation: 'stage_succeeded'
      readonly stage: RecoveryStage
      readonly source_event_id: string
      readonly stage_available_event_id: string
      readonly stage_elapsed_ms: number
      readonly state_consistent?: boolean
      readonly applied_session_id?: string
      readonly applied_chat_id?: string
    }
  | ({
      readonly observation: 'item_disposition'
      readonly item_declaration_event_id: string
      readonly stage_available_event_id: string
      readonly source_event_id: string
    } & RecoveryItem &
      RecoveryDisposition)
  | ({
      readonly observation: 'controls_observed'
      readonly client_view_id: string
      readonly item_declaration_event_id: string
      readonly stage_available_event_id: string
      readonly state_application_event_id: string
      readonly source_event_id: string
      readonly control_scenario: 'active' | 'settled_writable' | 'passive' | 'read_only'
    } & RecoveryControls)
  | {readonly observation: 'blocked' | 'failed'; readonly stage: RecoveryStage; readonly reason: RecoveryReason}
  | {
      readonly observation: 'completed'
      readonly reconnect_event_id: string
      readonly state_application_event_id: string
      readonly pending_commands_event_id: string
      readonly client_tools_event_id: string
      readonly controls_event_id: string
      readonly closure_time_ms: number
    }

export type ClientRecoveryFact = typeof CLIENT_RECOVERY_POLICY &
  RecoveryBinding & {
    readonly source_id: string
    readonly episode_id: string
    readonly episode_event_id: string
    readonly required_obligations: 'reconnect|state_application|pending_commands|client_tools|controls'
    readonly event_id: string
    readonly clock_id: string
    readonly sequence: number
    readonly observed_at_ms: number
    readonly monotonic_ms: number
    readonly episode_monotonic_ms: number
    readonly connection_generation?: string
    readonly attempt_event_id?: string
    readonly trigger: RecoveryTrigger
    readonly recovery_path: RecoveryPath
    readonly surface: UiReadinessSurface
    readonly role: RecoveryRole
    readonly visibility: UiReadinessVisibility
  } & RecoveryObservation

export type ClientRecoveryCoverageFact = typeof CLIENT_RECOVERY_POLICY &
  RecoveryBinding & {
    readonly observation: 'coverage_lost'
    readonly evidence_scope: 'source' | 'view'
    readonly reason: 'observation_capacity' | 'entry_unverified'
    readonly source_id: string
    readonly client_view_id?: string
    readonly event_id: string
    readonly source_event_id: string
    readonly clock_id: string
    readonly sequence: number
    readonly observed_at_ms: number
    readonly monotonic_ms: number
    readonly surface: UiReadinessSurface
    readonly role: RecoveryRole
    readonly visibility: UiReadinessVisibility
    readonly suppressed_observations: number
  }

export type ClientRecoveryEvent = ClientRecoveryFact | ClientRecoveryCoverageFact
